import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { API_URL } from "../../actions/auth";
import axios from "axios";

import Sidebar from "../../components/Sidebar";

const STATUS = {
  PRESENT: "PRESENT",
  ABSENT: "ABSENT",
  LATE: "LATE",
};

function formatDate(date) {
  return date.toISOString().split("T")[0];
}

function formatDisplayDate(dateString) {
  if (!dateString) return "";

  return new Date(`${dateString}T00:00:00`).toLocaleDateString(
    undefined,
    {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );
}

export default function AttendancePage() {
  const [selectedDate, setSelectedDate] = useState(
    formatDate(new Date())
  );

  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const [clsdivs, setClsdivs] = useState({});
  const [att_cls, setClass] = useState("");
  const [att_div, setDivision] = useState("");

  const state = useSelector((state) => state);

  useEffect(()=>{
    setLoading(true);

    fetch(API_URL + "/get_class_division?user_id=" + state.auth.user.user_id)
    .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json()
      })
      .then((result) => {
        setClsdivs(result['cds'])
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  },[])

  // ------------------------------------------------------------
  // Fetch attendance whenever the selected date changes
  // ------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    let user_id = 'CHS25158'

    async function loadAttendance() {
      setLoading(true);
      setError("");
      setSaved(false);

      try {
        console.log(att_cls,att_div,selectedDate)
        const response = await fetch(API_URL + "/get_attendance_teacher",{
            method: "POST",
            headers: {
                 "Content-Type": "application/json",
            },
            body: JSON.stringify({
                class_id: att_cls,
                division: att_div,
                doa: selectedDate
            }),
         }
        );

        if (!response.ok) {
          throw new Error("Unable to load attendance.");
        }

        const data = await response.json();

        if (cancelled) return;

        /*
          Expected API response:

          {
            students: [
              {
                id: 1,
                name: "John Doe",
                rollNumber: "ST001",
                avatarUrl: null
              }
            ],
            attendance: {
              "1": "present",
              "2": "absent"
            }
          }
        */

        setStudents(data.students || []);
        setAttendance(data.attendance || {});
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Something went wrong.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadAttendance();

    return () => {
      cancelled = true;
    };
  }, [selectedDate,att_div]);

  // ------------------------------------------------------------
  // Derived data
  // ------------------------------------------------------------

  const filteredStudents = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return students;

    return students.filter((student) => {
      return (
        student.name.toLowerCase().includes(query) ||
        String(student.rollNumber || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [students, search]);

  const counts = useMemo(() => {
    return students.reduce(
      (result, student) => {
        const status = attendance[student.id];

        if (status === STATUS.PRESENT) result.present++;
        if (status === STATUS.ABSENT) result.absent++;
        if (status === STATUS.LATE) result.late++;

        return result;
      },
      {
        present: 0,
        absent: 0,
        late: 0,
      }
    );
  }, [students, attendance]);

  const markedCount =
    counts.present + counts.absent + counts.late;

  const unmarkedCount = students.length - markedCount;

  // ------------------------------------------------------------
  // Attendance actions
  // ------------------------------------------------------------

  function handleClassChange(c) {
    setClass(c)
    // setLoading(true)
    // getStudents(c, att_div);
  }

  function handleDivisionChange(d) {
    setDivision(d);
    //setLoading(true);
    // getStudents(att_cls, d);
  }
  
  function getStudents(cla, div) {
    var body = {};
    const config = {
      headers: {
        Authorization: state.auth.token,

        "Content-Type": "application/json",
      },
    };
    body["class_id"] = cla;
    body["division"] = div;
    body["institution_id"] = state.auth.user.institution_id;
    body = JSON.stringify(body);

    axios
      .post(API_URL + "/get_students_list_class_division", body, config)
      .then((res) => {
        console.log(res.data);
        if (res.data.length !== 0) {
        }
        setLoading(false);
      });
  }

  function updateStatus(studentId, status) {
    setAttendance((current) => ({
      ...current,
      [studentId]: status,
    }));

    setSaved(false);
  }

  function markAll(status) {
    const next = {};

    students.forEach((student) => {
      next[student.id] = status;
    });

    setAttendance(next);
    setSaved(false);
  }

  async function saveAttendance() {
    setSaving(true);
    setError("");
    setSaved(false);

    try {
      const records = students.map((student) => ({
        studentId: student.id,
        status: attendance[student.id] || null,
      }));

      const response = await fetch(API_URL + "/add_attendance_teacher", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          class_id: att_cls,
          division: att_div,
          doa: selectedDate,
          tid: state.auth.user.user_id,
          attendance: records,
        }),
      });

      if (!response.ok) {
        throw new Error("Unable to save attendance.");
      }

      setSaved(true);
    } catch (err) {
      setError(err.message || "Unable to save attendance.");
    } finally {
      setSaving(false);
    }
  }

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <>
    <Sidebar />
    <div className="min-h-screen bg-slate-50" style={{margin: "66px 0 0 66px", borderTop: "1px solid #cdc"}}>
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="mt-1 text-sm text-slate-500">
                Manage student attendance for the selected Class, Section, Day ..
              </p>
            </div>

            <div className="flex items-center gap-3">
              <label
                htmlFor="att_cls"
                className="text-sm font-medium text-slate-600 hidden"
              >
                Class
              </label>

              <select
                id="att_cls"
                value={att_cls}
                onChange={(e) => handleClassChange(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              >
              <option value="">Select Class</option>
              {Object.keys(clsdivs).map((key) => (
                <option value={key}> {key} </option>
              ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <label
                htmlFor="att_div"
                className="text-sm font-medium text-slate-600 hidden"
              >
                Division
              </label>

              <select
                id="att_div"
                value={att_div}
                onChange={(e) => handleDivisionChange(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              >
              <option value="">Select Section</option>
              {att_cls && clsdivs[att_cls].map((key) => (
                <option value={key}> {key} </option>
              ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <label
                htmlFor="attendance-date"
                className="text-sm font-medium text-slate-600 hidden"
              >
                Date
              </label>

              <input
                id="attendance-date"
                type="date"
                value={selectedDate}
                onChange={(event) =>
                  setSelectedDate(event.target.value)
                }
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Date heading */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900">
            {formatDisplayDate(selectedDate)}
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            {students.length} students
          </p>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard
            label="Present"
            value={counts.present}
            color="emerald"
          />

          <StatCard
            label="Absent"
            value={counts.absent}
            color="red"
          />

          <StatCard
            label="Late"
            value={counts.late}
            color="amber"
          />

          <StatCard
            label="Unmarked"
            value={unmarkedCount}
            color="slate"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <span className="mt-0.5">!</span>

            <div className="flex-1">{error}</div>

            <button
              type="button"
              onClick={() => setError("")}
              className="font-medium hover:text-red-900"
            >
              ×
            </button>
          </div>
        )}

        {/* Main card */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {/* Toolbar */}
          <div className="border-b border-slate-200 p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              {/* Search */}
              <div className="relative w-full lg:max-w-sm">
                <svg
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.817-4.817A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>

                <input
                  type="search"
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search students..."
                  className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              {/* Bulk actions */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-medium uppercase tracking-wide text-slate-400">
                  Mark all
                </span>

                <button
                  type="button"
                  onClick={() => markAll(STATUS.PRESENT)}
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100"
                >
                  Present
                </button>

                <button
                  type="button"
                  onClick={() => markAll(STATUS.ABSENT)}
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
                >
                  Absent
                </button>

                <button
                  type="button"
                  onClick={() => markAll(STATUS.LATE)}
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 transition hover:bg-amber-100"
                >
                  Late
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <LoadingState />
          ) : students.length === 0 ? (
            <EmptyState />
          ) : filteredStudents.length === 0 ? (
            <NoResultsState />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Student
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredStudents.map((student) => (
                      <StudentRow
                        key={student.id}
                        student={student}
                        status={attendance[student.id]}
                        onStatusChange={updateStatus}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile list */}
              <div className="divide-y divide-slate-100 md:hidden">
                {filteredStudents.map((student) => (
                  <MobileStudentRow
                    key={student.id}
                    student={student}
                    status={attendance[student.id]}
                    onStatusChange={updateStatus}
                  />
                ))}
              </div>
            </>
          )}

          {/* Footer */}
          {!loading && students.length > 0 && (
            <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="text-sm text-slate-500">
                {markedCount} of {students.length} students marked
              </div>

              <div className="flex items-center gap-3">
                {saved && (
                  <span className="text-sm font-medium text-emerald-600">
                    Attendance saved
                  </span>
                )}

                <button
                  type="button"
                  disabled={saving}
                  onClick={saveAttendance}
                  className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <Spinner />
                      Saving...
                    </>
                  ) : (
                    "Save attendance"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
    </>
  );
}

// ------------------------------------------------------------
// Components
// ------------------------------------------------------------

function StatCard({ label, value, color }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-500">
          {label}
        </p>

        <span
          className={`rounded-full px-2 py-1 text-xs font-semibold ${colors[color]} hidden`}
        >
          {label}
        </span>
      </div>

      <p className="mt-2 text-2xl font-bold text-slate-900">
        {value}
      </p>
    </div>
  );
}

function StudentRow({
  student,
  status,
  onStatusChange,
}) {
  return (
    <tr className="transition hover:bg-slate-50">
      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center">
          <Avatar student={student} />

          <div className="ml-3">
            <div className="font-medium text-slate-900">
              {student.name}
            </div>

            {student.rollNumber && (
              <div className="text-sm text-slate-500">
                {student.rollNumber}
              </div>
            )}
          </div>
        </div>
      </td>

      <td className="px-6 py-4">
        <StatusSelector
          status={status}
          studentId={student.id}
          onChange={onStatusChange}
        />
      </td>
    </tr>
  );
}

function MobileStudentRow({
  student,
  status,
  onStatusChange,
}) {
  return (
    <div className="p-4">
      <div className="mb-3 flex items-center">
        <Avatar student={student} />

        <div className="ml-3">
          <div className="font-medium text-slate-900">
            {student.name}
          </div>

          {student.rollNumber && (
            <div className="text-sm text-slate-500">
              {student.rollNumber}
            </div>
          )}
        </div>
      </div>

      <StatusSelector
        status={status}
        studentId={student.id}
        onChange={onStatusChange}
      />
    </div>
  );
}

function StatusSelector({ status, studentId, onChange, }) {
  const options = [
    {
      value: STATUS.PRESENT,
      label: "Present",
      activeClass:
        "bg-emerald-500 text-white border-emerald-500",
    },
    {
      value: STATUS.ABSENT,
      label: "Absent",
      activeClass:
        "bg-red-500 text-white border-red-500",
    },
    {
      value: STATUS.LATE,
      label: "Late",
      activeClass:
        "bg-amber-500 text-white border-amber-500",
    },
  ];

  return (
    <div className="flex gap-2">
      {options.map((option) => {
        const active = status === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() =>
              onChange(studentId, option.value)
            }
            className={[
              "rounded-lg border px-3 py-2 text-sm font-medium transition",
              active
                ? option.activeClass
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
            ].join(" ")}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function Avatar({ student }) {
  if (student.avatarUrl) {
    return (
      <img
        src={student.avatarUrl}
        alt=""
        className="h-10 w-10 rounded-full object-cover"
      />
    );
  }

  const initials = student.name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
      {initials}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="divide-y divide-slate-100">
      {[1, 2, 3, 4, 5].map((item) => (
        <div
          key={item}
          className="flex items-center justify-between px-6 py-5"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 animate-pulse rounded-full bg-slate-200" />

            <div>
              <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-3 w-20 animate-pulse rounded bg-slate-100" />
            </div>
          </div>

          <div className="h-9 w-56 animate-pulse rounded-lg bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-16 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
        <span className="text-xl">👥</span>
      </div>

      <h3 className="mt-4 text-sm font-semibold text-slate-900">
        No students found
      </h3>

      <p className="mt-1 text-sm text-slate-500">
        There are no students to manage for this class.
      </p>
    </div>
  );
}

function NoResultsState() {
  return (
    <div className="px-6 py-16 text-center">
      <h3 className="text-sm font-semibold text-slate-900">
        No matching students
      </h3>

      <p className="mt-1 text-sm text-slate-500">
        Try a different name or roll number.
      </p>
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="mr-2 h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="3"
        className="opacity-25"
      />

      <path
        d="M21 12a9 9 0 00-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

